import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Modal from '../../components/Modal';
import LoadingSpinner from '../../components/LoadingSpinner';
import { vendorApi } from '../../services/vendor';
import type { GuardListItem, SiteListItem } from '../../types';
import extractApiError from '../../utils/extractApiError';
import { toGGScore, getScoreColor, getScoreColorClass } from '../../utils/score';

export default function VendorGuardAssignment() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [siteFilter, setSiteFilter] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Modal state
  const [assignGuard, setAssignGuard] = useState<GuardListItem | null>(null);
  const [reassignGuard, setReassignGuard] = useState<{ guard: GuardListItem; fromSiteId: string } | null>(null);
  const [unassignGuard, setUnassignGuard] = useState<{ guard: GuardListItem; siteId: string; siteName: string } | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedAssignSiteIds, setSelectedAssignSiteIds] = useState<string[]>([]);

  // Assignment page needs the FULL roster to pick from, not just the
  // default paginated slice.  Backend caps page_size at 1000
  // (VendorRosterPagination.max_page_size); vendors above that would
  // need real paging here.
  const { data: guardsData, isLoading: guardsLoading } = useQuery({
    queryKey: ['vendor', 'guards', 'assignment'],
    queryFn: () => vendorApi.getGuards({ page_size: '1000' }).then((r) => r.data),
  });

  const { data: sitesData } = useQuery({
    queryKey: ['vendor', 'sites', 'assignment'],
    queryFn: () => vendorApi.getSites({ vendor_assignment_status: 'active', page_size: '1000' }).then((r) => r.data),
  });

  const guards = guardsData?.results ?? [];
  const sites: SiteListItem[] = sitesData?.results ?? [];

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['vendor', 'guards'] });
    queryClient.invalidateQueries({ queryKey: ['vendor', 'sites'] });
  };

  const assignMutation = useMutation({
    mutationFn: ({ guardId, siteIds }: { guardId: string; siteIds: string[] }) =>
      vendorApi.assignGuardToSites(guardId, siteIds),
    onSuccess: () => { invalidateAll(); showToast('Guard assigned successfully.', 'success'); setAssignGuard(null); setSelectedAssignSiteIds([]); },
    onError: (err: unknown) => showToast(extractApiError(err), 'error'),
  });

  const unassignMutation = useMutation({
    mutationFn: ({ guardId, siteId }: { guardId: string; siteId: string }) =>
      vendorApi.unassignGuard(guardId, siteId),
    onSuccess: () => { invalidateAll(); showToast('Guard unassigned successfully.', 'success'); setUnassignGuard(null); },
    onError: (err: unknown) => showToast(extractApiError(err), 'error'),
  });

  const reassignMutation = useMutation({
    mutationFn: ({ guardId, fromSiteId, toSiteId }: { guardId: string; fromSiteId: string; toSiteId: string }) =>
      vendorApi.reassignGuard(guardId, fromSiteId, toSiteId),
    onSuccess: () => { invalidateAll(); showToast('Guard reassigned successfully.', 'success'); setReassignGuard(null); },
    onError: (err: unknown) => showToast(extractApiError(err), 'error'),
  });

  // For each guard, determine their first active site (from the guards list data we have assigned_sites_count but not the names)
  // We need detail for site info, but to keep it performant we'll load guard details on demand
  // For now, show basic info and use the assign/unassign/reassign modals

  const filteredGuards = siteFilter
    ? guards // We can't filter client-side without site data per guard; show all and let user act
    : guards;

  return (
    <>
      <PageHeader title="Guard Assignment" description="Assign, reassign, and unassign guards from sites" />

      {toast && (
        <div className={`mb-4 rounded-xl border px-4 py-3 ${toast.type === 'success' ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
          <p className={`text-sm ${toast.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>{toast.message}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Guard Cards (left 2/3) */}
        <div className="lg:col-span-2">
          {guardsLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : filteredGuards.length > 0 ? (
            <div className="space-y-3">
              {filteredGuards.map((guard) => {
                const isUnassigned = guard.assigned_sites_count === 0;
                return (
                  <div key={guard.id} className="rounded-2xl border border-slate-100 bg-white p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-white">
                        {guard.first_name?.[0]}{guard.last_name?.[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{guard.name}</p>
                      <p className="text-xs text-slate-400">
                        {isUnassigned ? 'Unassigned' : `${guard.assigned_sites_count} site${guard.assigned_sites_count !== 1 ? 's' : ''}`}
                      </p>
                    </div>
                    <span className={`text-sm font-bold ${getScoreColorClass(getScoreColor(guard.score))}`}>
                      {toGGScore(guard.score)}
                    </span>
                    <div className="flex gap-2">
                      {isUnassigned ? (
                        <button
                          onClick={() => { setAssignGuard(guard); setSelectedSiteId(''); setSelectedAssignSiteIds([]); }}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700"
                        >
                          Assign
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => { setReassignGuard({ guard, fromSiteId: '' }); setSelectedSiteId(''); }}
                            className="px-3 py-1.5 text-xs font-medium text-brand-600 bg-brand-50 rounded-lg hover:bg-brand-100"
                          >
                            Reassign
                          </button>
                          <button
                            onClick={() => setUnassignGuard({ guard, siteId: '', siteName: '' })}
                            className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                          >
                            Unassign
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
              <p className="text-sm text-slate-500">No guards registered yet.</p>
            </div>
          )}
        </div>

        {/* Site Overview Sidebar (right 1/3) */}
        <div className="rounded-2xl border border-slate-100 bg-white p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Sites Overview</h3>
          {sites.length > 0 ? (
            <div className="space-y-2">
              <button
                onClick={() => setSiteFilter(null)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!siteFilter ? 'bg-brand-50 text-brand-700 font-medium' : 'hover:bg-slate-50 text-slate-700'}`}
              >
                All Sites
              </button>
              {sites.map((site) => (
                <div
                  key={site.id}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${siteFilter === site.id ? 'bg-brand-50 text-brand-700 font-medium' : 'hover:bg-slate-50 text-slate-700'}`}
                  onClick={() => setSiteFilter(siteFilter === site.id ? null : site.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSiteFilter(siteFilter === site.id ? null : site.id); } }}
                >
                  <span className="block">{site.name}</span>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">{site.guard_count} guard{site.guard_count !== 1 ? 's' : ''}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/vendor/sites/${site.id}`); }}
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No active sites.</p>
          )}
        </div>
      </div>

      {/* Assign Modal */}
      <Modal isOpen={!!assignGuard} onClose={() => { setAssignGuard(null); setSelectedAssignSiteIds([]); }} title={`Assign ${assignGuard?.name}`} size="sm">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Select one or more sites to assign this guard to:</p>
          <div className="max-h-60 overflow-y-auto space-y-1 rounded-xl border-2 border-slate-200 p-3">
            {sites.length > 0 ? sites.map((s) => (
              <label key={s.id} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedAssignSiteIds.includes(s.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedAssignSiteIds((prev) => [...prev, s.id]);
                    } else {
                      setSelectedAssignSiteIds((prev) => prev.filter((id) => id !== s.id));
                    }
                  }}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-slate-900 block">{s.name}</span>
                  <span className="text-xs text-slate-400">{s.address}, {s.city}</span>
                </div>
              </label>
            )) : (
              <p className="text-sm text-slate-400 text-center py-2">No available sites.</p>
            )}
          </div>
          {selectedAssignSiteIds.length > 0 && (
            <p className="text-xs text-slate-500">{selectedAssignSiteIds.length} site{selectedAssignSiteIds.length !== 1 ? 's' : ''} selected</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => assignGuard && selectedAssignSiteIds.length > 0 && assignMutation.mutate({ guardId: assignGuard.id, siteIds: selectedAssignSiteIds })}
              disabled={selectedAssignSiteIds.length === 0 || assignMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50"
            >
              {assignMutation.isPending ? 'Assigning...' : `Assign to ${selectedAssignSiteIds.length || ''} Site${selectedAssignSiteIds.length !== 1 ? 's' : ''}`}
            </button>
            <button onClick={() => { setAssignGuard(null); setSelectedAssignSiteIds([]); }} className="px-4 py-2 text-sm text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
          </div>
        </div>
      </Modal>

      {/* Reassign Modal */}
      <Modal isOpen={!!reassignGuard} onClose={() => setReassignGuard(null)} title={`Reassign ${reassignGuard?.guard.name}`} size="sm">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">From Site</label>
            <select value={reassignGuard?.fromSiteId || ''} onChange={(e) => reassignGuard && setReassignGuard({ ...reassignGuard, fromSiteId: e.target.value })}
              className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
              <option value="">Select current site...</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">To Site</label>
            <select value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)}
              className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
              <option value="">Select target site...</option>
              {sites.filter((s) => s.id !== reassignGuard?.fromSiteId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => reassignGuard && reassignGuard.fromSiteId && selectedSiteId && reassignMutation.mutate({ guardId: reassignGuard.guard.id, fromSiteId: reassignGuard.fromSiteId, toSiteId: selectedSiteId })}
              disabled={!reassignGuard?.fromSiteId || !selectedSiteId || reassignMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50"
            >
              {reassignMutation.isPending ? 'Reassigning...' : 'Reassign'}
            </button>
            <button onClick={() => setReassignGuard(null)} className="px-4 py-2 text-sm text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
          </div>
        </div>
      </Modal>

      {/* Unassign Confirmation Modal */}
      <Modal isOpen={!!unassignGuard} onClose={() => setUnassignGuard(null)} title="Unassign Guard" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Select the site to unassign <strong>{unassignGuard?.guard.name}</strong> from:</p>
          <select value={unassignGuard?.siteId || ''} onChange={(e) => unassignGuard && setUnassignGuard({ ...unassignGuard, siteId: e.target.value })}
            className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
            <option value="">Select site...</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => unassignGuard && unassignGuard.siteId && unassignMutation.mutate({ guardId: unassignGuard.guard.id, siteId: unassignGuard.siteId })}
              disabled={!unassignGuard?.siteId || unassignMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50"
            >
              {unassignMutation.isPending ? 'Unassigning...' : 'Unassign'}
            </button>
            <button onClick={() => setUnassignGuard(null)} className="px-4 py-2 text-sm text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
          </div>
        </div>
      </Modal>
    </>
  );
}
